import { deepBlueSky } from "@/styles/theme";
import {
  Box,
  Button,
  Divider,
  Flex,
  FlexProps,
  Heading,
  HStack,
  List,
  ListItem,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { DEFAULT_FREE_TRIAL_DAYS } from "@root/types/freeTrial";
import currency from "currency.js";
import { useTranslations } from "next-intl";
import { FieldValues, UseFormWatch } from "react-hook-form";

// Map plan name from DB to translation key
const getPlanTranslationKey = (planName: string): string => {
  const mapping: Record<string, string> = {
    coach_basic: "coachBasic",
    coach_pro: "coachPro",
    coachingBusiness: "coachingBusiness",
  };
  return mapping[planName] || planName;
};

const variants = {
  canary: {
    bg: "brand.900",
    color: "brand.1000",
    borderColor: "brand.900",
    dividerColor: "brand.1000",
    buttonVariant: "secondary",
    tagBg: "brand.800",
  },
  blue: {
    bg: deepBlueSky,
    color: "white",
    borderColor: deepBlueSky,
    dividerColor: "white",
    buttonVariant: "secondary",
    tagBg: "brand.900",
  },
  black: {
    bg: "brand.1000",
    color: "white",
    borderColor: "brand.1000",
    dividerColor: "white",
    buttonVariant: "primary",
    tagBg: "brand.900",
  },
};

export const PricingBox = ({
  price,
  name,
  description,
  header,
  oldPrice,
  additionalCoachPrice,
  features,
  variant,
  platformId,
  freeTrialDays = DEFAULT_FREE_TRIAL_DAYS,
  onSelect,
  watch,
  cardProps,
}: {
  price: string;
  name: string;
  description: string;
  additionalCoachPrice?: string;
  header?: string;
  oldPrice?: string;
  features?: string[];
  variant: keyof typeof variants;
  platformId: string;
  freeTrialDays?: number;
  onSelect?: () => void;
  watch?: UseFormWatch<FieldValues>;
  cardProps?: FlexProps;
}) => {
  const t = useTranslations();
  const { bg, color, borderColor, dividerColor, buttonVariant, tagBg } =
    variants[variant];
  const selectedPlan = watch?.("selectedPlan");
  const selected = selectedPlan?.toString() === platformId.toString();
  
  const planKey = getPlanTranslationKey(name);
  const whatItForKey = `Pricing.${planKey}.whatItFor`;
  const idealForKey = `Pricing.${planKey}.idealFor`;
  const includesNoteKey = `Pricing.${planKey}.includesNote`;
  const scaleAsYouGrowKey = `Pricing.${planKey}.scaleAsYouGrow`;
  
  // Check if translations exist by comparing the result with the key
  const getTranslationSafe = (key: string): string | null => {
    try {
      const translation = t(key);
      // If translation returns the key itself or is empty, it doesn't exist
      if (translation === key || !translation || translation.trim() === "") {
        return null;
      }
      return translation;
    } catch {
      return null;
    }
  };
  
  const hasWhatItFor = getTranslationSafe(whatItForKey) !== null;
  const hasIdealFor = getTranslationSafe(idealForKey) !== null;
  const hasIncludesNote = getTranslationSafe(includesNoteKey) !== null;
  const hasScaleAsYouGrow = getTranslationSafe(scaleAsYouGrowKey) !== null;
  
  return (
    <Flex
      as="a"
      href={onSelect ? undefined : `/auth/sign-up?plan=${platformId}`}
      direction="column"
      cursor="pointer"
      flex={1}
      rounded="12px"
      bg={bg}
      color={color}
      border={selected ? "1px solid" : "none"}
      borderColor={borderColor}
      p={8}
      transition="all 0.3s ease-in-out"
      transform={selected ? "translateY(-2px)" : "none"}
      _hover={{
        transform: "translateY(-2px)",
      }}
      {...cardProps}
    >
      <Box w="full" flex={1}>
        <Box textAlign="left">
          <Heading fontSize="1.75rem">{t(`plans.${name}`)}</Heading>
          <HStack justifyContent="space-between">
            <Text>{description}</Text>
          </HStack>
        </Box>

        <Box my={6}>
          <Divider borderColor={dividerColor} />
        </Box>

        {hasWhatItFor && (
          <VStack align="stretch" mb={4} spacing={2}>
            <Text fontWeight={600} fontSize="sm">
              {t("Pricing.whatItFor")}
            </Text>
            <Text fontSize="sm">{t(whatItForKey)}</Text>
          </VStack>
        )}

        {hasIncludesNote && (
          <Text fontSize="sm" mb={2} fontWeight={500}>
            {t(includesNoteKey)}
          </Text>
        )}

        {!!additionalCoachPrice && (
          <HStack justifyContent="space-between" mb={2}>
            <Text>Additional Team Coach</Text>
            <Text size="sm">+ {additionalCoachPrice}</Text>
          </HStack>
        )}
        {(planKey === "coachBasic" || planKey === "coachingBusiness") && (
          <Text fontWeight={600} fontSize="sm" mb={2}>
            {t("Pricing.includes")}
          </Text>
        )}
        <List mb={hasIdealFor || hasScaleAsYouGrow ? 6 : 12} listStyleType={"'- '"} textAlign="left" pl={4}>
          {features?.map((feature) => (
            <ListItem key={feature}>{t(feature)}</ListItem>
          ))}
        </List>

        {hasIdealFor && (
          <VStack align="stretch" mb={hasScaleAsYouGrow ? 4 : 0} spacing={2}>
            <Text fontWeight={600} fontSize="sm">
              {t("Pricing.idealFor")}
            </Text>
            <Text fontSize="sm">{t(idealForKey)}</Text>
          </VStack>
        )}

        {hasScaleAsYouGrow && (
          <VStack align="stretch" mb={6} spacing={2}>
            <Text fontWeight={600} fontSize="sm">
              {t("Pricing.scaleAsYouGrow")}
            </Text>
            <Text fontSize="sm">{t(scaleAsYouGrowKey)}</Text>
          </VStack>
        )}
      </Box>
      <Box mb={4}>
        {!!oldPrice && (
          <Text textDecoration={"line-through"}>
            {t("before")} {oldPrice}
          </Text>
        )}
        <Heading fontWeight={500} fontFamily={"Gotham"}>
          {currency(price).format({
            symbol: "€",
            precision: 2,
          })}
        </Heading>
      </Box>
      <HStack justifyContent="space-between" w="full">
        <Button
          flex={1}
          rounded="full"
          w={{
            base: "full",
            md: "auto",
          }}
          variant={buttonVariant}
          _hover={{
            bgColor: "brand.800",
            color: "brand.1000",
          }}
          fontWeight={600}
          onClick={onSelect}
          textTransform={"capitalize"}
        >
          {selected ? t("selected") : t("startFreeTrial")}
        </Button>
        <Box w="120px" textAlign="right">
          <Tag variant={buttonVariant} fontWeight={600}>
            {t("xFreeTrial", { days: freeTrialDays })}
          </Tag>
          {selected && (
            <Tag bgColor={tagBg} color="brand.1000" size="sm" fontWeight={600}>
              {t("selected")}
            </Tag>
          )}
        </Box>
      </HStack>
    </Flex>
  );
};
